import { siteApi } from "./api";
import config from "./config";
import { getStoredAuthToken } from "./authStorage";
import {
  createRecentGameItem,
  createRecentProviderItem,
  recordRecentPlayItem,
} from "../utils/recentPlayHistory";

export const GAME_PLAYER_PATH = "/play";
export const GAME_LAUNCH_EVENT = "crypto-game-launch-open";
export const GAME_LAUNCH_STORAGE_KEY = `${config.appName}_active_game_launch`;
export const GAME_LAUNCH_LOCK_KEY = `${config.appName}_active_game_lock`;
export const GAME_MINI_PLAYER_EVENT = "crypto-game-mini-player-change";
export const GAME_MINI_PLAYER_STORAGE_KEY = `${config.appName}_mini_game_player`;
export const GAME_MINI_PLAYER_DEFAULT_SIZE = {
  width: 640,
  height: 390,
};

const GAME_LAUNCH_LOCK_TTL_MS = 15_000;
const GAME_LAUNCH_HEARTBEAT_MS = 5_000;
const GAME_LAUNCH_OWNER_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getDeviceType = () => {
  if (typeof window === "undefined") return "desktop";
  return window.innerWidth <= 768 ? "mobile" : "desktop";
};

const readLanguage = () => {
  if (typeof window === "undefined") return "ko";

  try {
    return (
      localStorage.getItem(`${config.appName}_lang`) ||
      sessionStorage.getItem(`${config.appName}_lang`) ||
      "ko"
    );
  } catch {
    return "ko";
  }
};

const extractPlayUrl = (response) => {
  const data = response?.data;
  if (typeof data === "string" && /^https?:\/\//i.test(data)) return data;
  return data?.url || data?.game_url || data?.launch_url || "";
};

const getReadableError = (response, fallback) => {
  if (response?.error === "Not authenticated") return "로그인 후 이용해주세요.";
  return response?.error || response?.message || fallback;
};

const getNow = () => Date.now();

const dispatchAuthRequired = () => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("crypto-auth-required", {
      detail: { modalOption: "login", reason: "game-launch" },
    })
  );
};

const ensureCanLaunchGame = () => {
  if (getStoredAuthToken()) return true;

  dispatchAuthRequired();
  return false;
};

const readJsonStorage = (storage, key) => {
  try {
    const rawValue = storage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
};

const getSourcePath = () => {
  if (typeof window === "undefined") return "/";
  const { pathname, search, hash } = window.location;
  const currentPath = `${pathname || "/"}${search || ""}${hash || ""}`;
  return currentPath === GAME_PLAYER_PATH ? "/" : currentPath;
};

export const readStoredGameLaunch = () => {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = sessionStorage.getItem(GAME_LAUNCH_STORAGE_KEY);
    if (!rawValue) return null;
    const payload = JSON.parse(rawValue);
    if (!payload?.playUrl) return null;
    return payload;
  } catch {
    return null;
  }
};

export const clearStoredGameLaunch = () => {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.removeItem(GAME_LAUNCH_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in private contexts; navigation still works.
  }
};

const dispatchMiniPlayerChange = (detail = {}) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(GAME_MINI_PLAYER_EVENT, { detail }));
};

export const readStoredMiniGamePlayer = () => {
  if (typeof window === "undefined") return null;

  const state = readJsonStorage(sessionStorage, GAME_MINI_PLAYER_STORAGE_KEY);
  return state?.active ? state : null;
};

export const activateMiniGamePlayer = (state = {}) => {
  if (typeof window === "undefined") return;

  const nextState = {
    active: true,
    width: state.width || GAME_MINI_PLAYER_DEFAULT_SIZE.width,
    height: state.height || GAME_MINI_PLAYER_DEFAULT_SIZE.height,
    updatedAt: getNow(),
  };

  try {
    sessionStorage.setItem(GAME_MINI_PLAYER_STORAGE_KEY, JSON.stringify(nextState));
  } catch {
    // Mini player is an enhancement; regular player route still works without storage.
  }

  dispatchMiniPlayerChange(nextState);
};

export const deactivateMiniGamePlayer = () => {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.removeItem(GAME_MINI_PLAYER_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }

  dispatchMiniPlayerChange({ active: false });
};

const writeGameLaunchLock = (lock) => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(GAME_LAUNCH_LOCK_KEY, JSON.stringify(lock));
  } catch {
    // If cross-tab storage is unavailable, launch still falls back to same-tab behavior.
  }
};

const clearGameLaunchLock = () => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(GAME_LAUNCH_LOCK_KEY);
  } catch {
    // Ignore storage failures; the TTL guard will recover on supported browsers.
  }
};

export const readActiveGameLaunchLock = (now = getNow()) => {
  if (typeof window === "undefined") return null;

  const lock = readJsonStorage(localStorage, GAME_LAUNCH_LOCK_KEY);
  if (!lock?.ownerId || !lock?.updatedAt) return null;

  if (now - Number(lock.updatedAt) > GAME_LAUNCH_LOCK_TTL_MS) {
    clearGameLaunchLock();
    return null;
  }

  return lock;
};

export const isGameLaunchLockedByAnotherWindow = () => {
  const lock = readActiveGameLaunchLock();
  return Boolean(lock && lock.ownerId !== GAME_LAUNCH_OWNER_ID);
};

const reserveGameLaunchLock = (label) => {
  const activeLock = readActiveGameLaunchLock();
  if (activeLock && activeLock.ownerId !== GAME_LAUNCH_OWNER_ID) {
    return { success: false, lock: activeLock };
  }

  const now = getNow();
  writeGameLaunchLock({
    ownerId: GAME_LAUNCH_OWNER_ID,
    label: label || "게임",
    state: "launching",
    openedAt: now,
    updatedAt: now,
  });

  return { success: true };
};

export const releaseGameLaunchLock = () => {
  const activeLock = readActiveGameLaunchLock();
  if (!activeLock || activeLock.ownerId === GAME_LAUNCH_OWNER_ID) {
    clearGameLaunchLock();
  }
};

const activateGameLaunchLock = (payload) => {
  const activeLock = readActiveGameLaunchLock();
  if (activeLock && activeLock.ownerId !== GAME_LAUNCH_OWNER_ID) {
    return false;
  }

  const now = getNow();
  writeGameLaunchLock({
    ownerId: GAME_LAUNCH_OWNER_ID,
    label: payload?.label || "게임",
    kind: payload?.kind || "game",
    gameId: payload?.gameId || "",
    lobbyId: payload?.lobbyId || "",
    state: "playing",
    openedAt: activeLock?.openedAt || payload?.openedAt || now,
    updatedAt: now,
  });

  return true;
};

const showDuplicateGameLaunchAlert = () => {
  if (typeof window === "undefined") return;
  window.alert("이미 다른 창에서 게임이 실행 중입니다. 기존 게임을 종료한 뒤 다시 시도해주세요.");
};

const writeStoredGameLaunch = (payload) => {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.setItem(GAME_LAUNCH_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // The player route will show a recovery state if storage is unavailable.
  }
};

const openInAppPlayer = (payload) => {
  if (typeof window === "undefined") return false;

  if (!activateGameLaunchLock(payload)) {
    showDuplicateGameLaunchAlert();
    return false;
  }

  writeStoredGameLaunch(payload);

  const launchEvent = new CustomEvent(GAME_LAUNCH_EVENT, {
    cancelable: true,
    detail: payload,
  });
  window.dispatchEvent(launchEvent);

  if (!launchEvent.defaultPrevented) {
    window.location.assign(GAME_PLAYER_PATH);
  }

  return true;
};

const buildLaunchPayload = ({ playUrl, label, kind, gameId, lobbyId, vendor }) => ({
  playUrl,
  label: label || "게임",
  kind,
  gameId: gameId || "",
  lobbyId: lobbyId || "",
  vendorName:
    vendor?.vendor_name_kr ||
    vendor?.vendor_name_ko ||
    vendor?.vendor_name ||
    vendor?.name ||
    "",
  sourcePath: getSourcePath(),
  openedAt: Date.now(),
});

export const launchProviderLobby = async (vendor, options = {}) => {
  const label =
    options.label ||
    vendor?.vendor_name_kr ||
    vendor?.vendor_name_ko ||
    vendor?.vendor_name ||
    "게임";
  const lobbyId = vendor?.vendor_list?.lobby_id || vendor?.lobby_id || "";

  if (!lobbyId) {
    window.alert(`${label} 로비 정보를 불러오지 못했습니다.`);
    return { success: false, error: "missing_lobby_id" };
  }

  if (!ensureCanLaunchGame()) {
    return { success: false, error: "auth_required" };
  }

  const lockReservation = reserveGameLaunchLock(label);
  if (!lockReservation.success) {
    showDuplicateGameLaunchAlert();
    return { success: false, error: "duplicate_game_running" };
  }

  try {
    const response = await siteApi.playGame({
      lobby_id: lobbyId,
      device: getDeviceType(),
      language: options.language || readLanguage(),
    });
    const playUrl = extractPlayUrl(response);

    if (response.success && playUrl) {
      const launchPayload = buildLaunchPayload({
        playUrl,
        label,
        kind: "provider",
        lobbyId,
        vendor,
      });

      if (!openInAppPlayer(launchPayload)) {
        return { success: false, error: "duplicate_game_running" };
      }

      recordRecentPlayItem(createRecentProviderItem(vendor, { ...options, label, lobbyId }));
      return { success: true, mode: "in_app", playUrl };
    }

    releaseGameLaunchLock();
    window.alert(getReadableError(response, `${label} 실행에 실패했습니다.`));
    return { success: false, error: response.error || response.message || "launch_failed" };
  } catch {
    releaseGameLaunchLock();
    window.alert(`${label} 실행 중 오류가 발생했습니다.`);
    return { success: false, error: "launch_error" };
  }
};

export const launchGameById = async (gameId, options = {}) => {
  const label = options.label || "게임";

  if (!gameId) {
    window.alert("게임 정보를 불러오지 못했습니다.");
    return { success: false, error: "missing_game_id" };
  }

  if (!ensureCanLaunchGame()) {
    return { success: false, error: "auth_required" };
  }

  const lockReservation = reserveGameLaunchLock(label);
  if (!lockReservation.success) {
    showDuplicateGameLaunchAlert();
    return { success: false, error: "duplicate_game_running" };
  }

  try {
    const response = await siteApi.playGame({
      game_id: gameId,
      device: getDeviceType(),
      language: options.language || readLanguage(),
    });
    const playUrl = extractPlayUrl(response);

    if (response.success && playUrl) {
      const launchPayload = buildLaunchPayload({
        playUrl,
        label,
        kind: "game",
        gameId,
        vendor: options.vendor,
      });

      if (!openInAppPlayer(launchPayload)) {
        return { success: false, error: "duplicate_game_running" };
      }

      recordRecentPlayItem(createRecentGameItem(gameId, options));
      return { success: true, mode: "in_app", playUrl };
    }

    releaseGameLaunchLock();
    window.alert(getReadableError(response, `${label} 실행에 실패했습니다.`));
    return { success: false, error: response.error || response.message || "launch_failed" };
  } catch {
    releaseGameLaunchLock();
    window.alert(`${label} 실행 중 오류가 발생했습니다.`);
    return { success: false, error: "launch_error" };
  }
};

export const startGameLaunchHeartbeat = (payload, options = {}) => {
  if (typeof window === "undefined" || !payload?.playUrl) {
    return { success: false, cleanup: () => {} };
  }

  if (!activateGameLaunchLock(payload)) {
    return { success: false, lock: readActiveGameLaunchLock(), cleanup: () => {} };
  }

  const heartbeat = () => {
    activateGameLaunchLock(payload);
  };
  const intervalId = window.setInterval(heartbeat, GAME_LAUNCH_HEARTBEAT_MS);
  const handleUnload = () => {
    releaseGameLaunchLock();
  };

  window.addEventListener("pagehide", handleUnload);
  window.addEventListener("beforeunload", handleUnload);

  return {
    success: true,
    cleanup: () => {
      window.clearInterval(intervalId);
      window.removeEventListener("pagehide", handleUnload);
      window.removeEventListener("beforeunload", handleUnload);
      if (options.releaseOnCleanup !== false) {
        releaseGameLaunchLock();
      }
    },
  };
};
